/**
 * Transpose — a row of semitone buttons that set the ONE Ableton transpose
 * (OP-AbletonTranspose) via OSC.
 *
 * Radio-style: the active cell reflects the session-wide concert transpose
 * (`transpose/session.ts`), NOT per-item state — so a Transpose row and any
 * Capo rows stay in lock-step automatically. Tapping a cell sets the concert
 * transpose (a signal, not a store write — no item remount) which dispatches
 * OSC `/OP-AbletonTranspose/transpose <n>` on the primary bridge.
 *
 * Edit mode yields to the wrapper (select/drag); test + performance modes fire.
 */

import { For, type Accessor } from 'solid-js';
import { editOwnsGesture, isEditMode, type TransposeItem, type LayoutColor } from '../layout/store.ts';
import { setConcertTranspose, lastTransposeSend } from '../transpose/session.ts';
import { setlistState, observedTranspose } from '../setlist/state.ts';
import { createDiscreteGuard } from '../performance/guard.ts';
import { TrayTitle, trayAttrs } from './TrayTitle.tsx';

interface TransposeProps {
  item: TransposeItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

/** "+7" / "0" / "-5" — explicit sign on positives so the row reads as a
 *  signed range at a glance. */
function semitoneLabel(n: number): string {
  return n > 0 ? `+${n}` : String(n);
}

export function Transpose(props: TransposeProps) {
  // Show-mode guard (SD-5): first tap on a semitone arms it (2s), second fires.
  const guard = createDiscreteGuard(() => !!props.item.guard);

  /**
   * What Live ACTUALLY has, or null when we cannot say.
   *
   * ⚠️ This replaces lighting from `concertTranspose()`, which was a
   * localStorage signal only WE ever wrote. The operator reported the
   * consequence directly: on an OPSetlist song change this row kept lighting
   * the previous song's value - *"they do not go blank, they go WRONG"* -
   * because nothing external could relight it and it could not even know it
   * was stale. Same rule as the metronome and OBS rows: light from an
   * authority, never from a local latch.
   *
   * Reads `last_sent`, not `current`. See `setlist/state.ts` - `current`
   * goes null on a song that carried no transpose, while Live is still
   * sitting wherever the previous song put it.
   */
  const observed = (): number | null => observedTranspose(setlistState());

  /**
   * Our own pending send, shown until the readback agrees - RecQuant's
   * convention, and it earns its place here for the same reason: the
   * round trip is OPSetlist -> OPBridge -> us, so a tap is not instantly
   * reflected and an unlit row would look like the tap was lost.
   *
   * ⚠️ Reads `lastTransposeSend()`, NOT `concertTranspose()`. Deriving this
   * from the latch meant a cell nobody pressed claimed to be an unconfirmed
   * send, because the latch is hydrated from localStorage and the readback
   * now changes every song - see the long note on `lastTransposeSend`.
   *
   * Cleared by comparison rather than by a timer: once `observed` equals what
   * we sent, the send is no longer pending; once a readback NEWER than the
   * send disagrees, the send lost. A stuck pending therefore means the
   * readback genuinely never agreed, which is information rather than a
   * cosmetic glitch.
   */
  const pending = (): number | null => {
    const send = lastTransposeSend();
    if (!send) return null;                       // nothing sent: a hydrated latch is not a send
    if (observed() === send.value) return null;   // the readback agreed
    const m = setlistState();
    if (m.status === 'ok' && m.appliedAt > send.at) return null;  // a song SENT after the press overwrote it (`appliedAt`: not the per-poll `at`, and not a held song the operator only scrolled to)
    return send.value;
  };

  /**
   * ⚠️ UNKNOWN IS A REAL STATE AND MUST NOT DEFAULT TO ZERO.
   *
   * Zero is a genuine transpose - the song explicitly asked for concert
   * pitch - and is indistinguishable from "we have not been told" the moment
   * it is defaulted. PLAN records this for the whole transpose arc: render
   * `--` for absent, never a default. Here that means: when `observed()` is
   * null, NO cell is lit, rather than the `0` cell being lit.
   */
  const unknown = (): boolean => observed() === null;

  const cells = (): number[] => {
    const out: number[] = [];
    for (let n = props.item.start; n <= props.item.end; n++) out.push(n);
    return out;
  };

  function onCellDown(n: number, e: PointerEvent) {
    if (editOwnsGesture(props.item.id)) return;
    e.stopPropagation();
    if (!guard.gate(String(n))) return; // guarded: first tap only armed
    // Sets the shared concert transpose (relights every Transpose + Capo
    // row) and dispatches the OSC to this row's own configured bridge(s)
    // (multi-select, 2026-08-13). Never throws; an offline target no-ops.
    setConcertTranspose(n, props.item.bridgeIds);
  }

  return (
    <div class="transpose" data-token={COLOR_TOKEN[props.item.color]} {...trayAttrs(props.item)}>
      <TrayTitle item={props.item} />
      <For each={cells()}>
        {(n) => (
          <button
            type="button"
            class="transpose-cell"
            data-active={observed() === n ? 'true' : 'false'}
            data-pending={pending() === n ? 'true' : 'false'}
            data-unknown={unknown() ? 'true' : 'false'}
            data-armed={guard.armedKey() === String(n) ? 'true' : 'false'}
            data-home={n === 0 ? 'true' : 'false'}
            data-semitone={n}
            onPointerDown={(e) => onCellDown(n, e)}
            title={`Transpose ${semitoneLabel(n)} semitone${Math.abs(n) === 1 ? '' : 's'}`}
          >
            {semitoneLabel(n)}
          </button>
        )}
      </For>
    </div>
  );
}
